import { useState, useEffect, useMemo } from "react";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import { ToggleOff as ToggleOffIcon, ToggleOn as ToggleOnIcon } from "@mui/icons-material";
import styled from "styled-components";
import { map, uniqBy, xor } from "lodash-es";
import { useTranslation } from "react-i18next";

import { FleetConfig as IFleetConfig, FleetConfigItem } from "src/types";
import { useFleetConfig } from "src/hooks";
import { CustomSwitch } from "./Switch";
import { TableHeadCell } from "./TableHeadCell";

interface Props {
  className?: string;
  lezId: number | string;
  onChange: (fleetConfig: IFleetConfig) => void;
  readOnly: boolean;
}

const INITIAL_FILTERS = {
  category: [],
  euro: [],
  fuel: [],
};
export type OrderColumn = "category" | "euro" | "fuel" | "allowed";
export type FilterColumn = "category" | "euro" | "fuel";
export type OrderDirection = "asc" | "desc";
export type Filters = Record<FilterColumn, string[]>;

export function FleetConfigTable(props: Props) {
  const { readOnly } = props;

  const { t } = useTranslation();
  const { data: fleetConfig } = useFleetConfig(props.lezId);

  // dirty data is non-persisted fleet config
  const [dirtyData, setDirtyData] = useState<IFleetConfig>([]);
  const [orderBy, setOrderBy] = useState<OrderColumn>("category");
  const [orderDirection, setOrderDirection] = useState<OrderDirection>("asc");
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);

  /**
   * Memo
   */

  const visualData = useMemo(() => {
    const filteredData = filterData(dirtyData, filters);
    const sortedData = sortData(filteredData, orderBy, orderDirection);
    return sortedData;
  }, [dirtyData, filters, orderBy, orderDirection]);

  const toggleDirection = useMemo((): "on" | "off" => {
    const enabledItems = visualData.filter((item) => !item.disabled);
    const allowedItems = enabledItems.filter((item) => item.allowed);

    if (allowedItems.length === enabledItems.length) return "off";
    else return "on";
  }, [visualData]);

  /**
   * Effects
   */
  // set dirty data to initial fleetconfig payload
  useEffect(() => {
    if (!fleetConfig) return;
    setDirtyData(fleetConfig);
  }, [fleetConfig]);

  // trigger onChange on dirty data change
  useEffect(() => {
    if (fleetConfig && dirtyData.length > 0 && fleetConfig !== dirtyData) {
      props.onChange(dirtyData);
    }
  }, [dirtyData, fleetConfig, props]);

  /**
   * Functions
   */
  function handleChange(updatedItem: FleetConfigItem) {
    const updatedData = dirtyData.map((item: FleetConfigItem) => {
      if (item.id === updatedItem.id) return updatedItem;
      return item;
    });
    setDirtyData(updatedData);
  }

  // toggle visual data allowed attribute
  function updateAllowance(allowance: boolean) {
    const updatedData = dirtyData.map((item) => {
      // if the item is disabled, do not update
      if (item.disabled) return item;

      // if the item is not in visualData, do not update
      if (!visualData.includes(item)) return item;

      // else update
      return { ...item, allowed: allowance };
    });
    setDirtyData(updatedData);
  }

  function handleSort(column: OrderColumn) {
    if (column !== orderBy) {
      setOrderBy(column);
      setOrderDirection("asc");
    }
    if (column === orderBy) {
      toggleOrderDirection();
    }
  }

  function toggleOrderDirection() {
    if (orderDirection === "asc") {
      setOrderDirection("desc");
    } else if (orderDirection === "desc") {
      setOrderDirection("asc");
    }
  }

  function updateFilters(filterKey: FilterColumn, updatedFilters: Array<string | number>) {
    setFilters({
      ...filters,
      [filterKey]: xor(filters[filterKey], updatedFilters),
    });
  }

  function fleetConfigItemSwitchLabel(item: FleetConfigItem) {
    if (readOnly) return t<string>("label.fleetConfigItemReadOnly");
    if (item.disabled) return t<string>("label.fleetConfigItemDisabled");
    else return "";
  }

  return render();

  function render() {
    return (
      <Styles className={props.className}>
        <TableContainer component={Paper}>
          <Table stickyHeader className="table">
            <TableHead>
              <TableRow>
                <TableHeadCell<OrderColumn>
                  dataKey="category"
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  tooltip={t("label.filterByVehicle")}
                  onSort={() => handleSort("category")}
                  onFilter={(filters) => updateFilters("category", filters)}
                  filters={uniqBy(
                    map(fleetConfig, (item) => ({
                      id: item.category,
                      label: item.categoryTranslation,
                    })),
                    "id"
                  )}
                  activeFilterIds={filters.category}
                >
                  {t("label.vehicle")}
                </TableHeadCell>

                <TableHeadCell<OrderColumn>
                  dataKey="euro"
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  tooltip={t("label.filterByEmission")}
                  onSort={() => handleSort("euro")}
                  onFilter={(filters) => updateFilters("euro", filters)}
                  filters={uniqBy(
                    map(fleetConfig, (item) => ({
                      id: item.euroTranslation,
                      label: item.euroTranslation,
                    })),
                    "id"
                  )}
                  activeFilterIds={filters.euro}
                >
                  {t("label.emission")}
                </TableHeadCell>

                <TableHeadCell<OrderColumn>
                  dataKey="fuel"
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  tooltip={t("label.filterByFuel")}
                  onSort={() => handleSort("fuel")}
                  onFilter={(filters) => updateFilters("fuel", filters)}
                  filters={uniqBy(
                    map(fleetConfig, (item) => ({
                      id: item.fuel,
                      label: item.fuelTranslation,
                    })),
                    "id"
                  )}
                  activeFilterIds={filters.fuel}
                >
                  {t("label.fuel")}
                </TableHeadCell>

                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === "allowed"}
                    direction={orderDirection}
                    onClick={() => handleSort("allowed")}
                  >
                    {!readOnly && (
                      <Tooltip title="Toggle all">
                        <>
                          {toggleDirection === "on" && (
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                updateAllowance(true);
                              }}
                              size="large"
                            >
                              <ToggleOnIcon fontSize="large" />
                            </IconButton>
                          )}
                          {toggleDirection === "off" && (
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                updateAllowance(false);
                              }}
                              size="large"
                            >
                              <ToggleOffIcon fontSize="large" />
                            </IconButton>
                          )}
                        </>
                      </Tooltip>
                    )}
                    {t("label.allowedInLEZ")}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visualData.map((row: FleetConfigItem) => (
                <TableRow key={row.id}>
                  <TableCell component="th" scope="row">
                    {row.categoryTranslation}
                  </TableCell>
                  <TableCell>{row.euroTranslation}</TableCell>
                  <TableCell>{row.fuelTranslation}</TableCell>
                  <TableCell align="right">
                    <Tooltip placement="left" title={fleetConfigItemSwitchLabel(row)}>
                      <div
                        style={{
                          display: "inline-block",
                          // opacity: readOnly ? 0.2 : 1,
                          // pointerEvents: readOnly ? "none" : "inherit",
                        }}
                      >
                        <CustomSwitch
                          readOnly={readOnly}
                          disabled={row.disabled}
                          checked={row.allowed}
                          onChange={() => handleChange({ ...row, allowed: !row.allowed })}
                        />
                      </div>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Styles>
    );
  }
}

const Styles = styled.div`
  .MuiTableContainer-root {
    height: 100%;
  }

  .MuiTableBody-root {
    .MuiTableRow-root {
      &:nth-of-type(odd) {
        background-color: #f7f7f7;
      }
    }
  }

  .MuiTableCell-stickyHeader {
    background-color: #fafafa;
  }
`;

function filterData(data: IFleetConfig, filters: Filters): IFleetConfig {
  return data.filter((item) => {
    const categoryMatch = filters.category.includes(item.category) || filters.category.length === 0;
    const euroMatch = filters.euro.includes(item.euroTranslation) || filters.euro.length === 0;
    const fuelMatch = filters.fuel.includes(item.fuel) || filters.fuel.length === 0;

    return categoryMatch && euroMatch && fuelMatch;
  });
}

function sortData(data: IFleetConfig, column: OrderColumn, direction: OrderDirection) {
  switch (column) {
    case "allowed":
      return sortAllowed(data, direction);
    default:
      return defaultSort(data, column, direction);
  }
}

function sortAllowed(data: IFleetConfig, direction: OrderDirection) {
  const sortedByAllowance = defaultSort(data, "allowed", direction);
  const sortedByDisabled = defaultSort(sortedByAllowance, "disabled", direction);
  return sortedByDisabled;
}

function defaultSort(data: IFleetConfig, column: OrderColumn | keyof FleetConfigItem, direction: OrderDirection) {
  return [...data].sort((a, b) => {
    const nameA = a[column].toString().toUpperCase(); // ignore upper and lowercase
    const nameB = b[column].toString().toUpperCase(); // ignore upper and lowercase
    if (direction === "asc") {
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
    }
    if (direction === "desc") {
      if (nameA > nameB) return -1;
      if (nameA < nameB) return 1;
    }

    // names must be equal
    return 0;
  });
}
